import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  type Firestore,
  type Query,
  type WriteBatch,
} from 'firebase/firestore'
import { CHECKLIST_CANVAS_NODES_COLLECTION, type ChecklistCanvasNode } from '../types/checklistCanvas'
import {
  checklistItemToCanvasNode,
  type ChecklistCanvasProjectionInput,
  type ChecklistCanvasProjectionOptions,
} from '../utils/checklistCanvasProjection'
import type { ChecklistItem } from '../types/schema'
import { isRestrictedIn, scopedReadConstraints } from './accessScopeService'

type FirestoreWriteData = Record<string, unknown>

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const stripUndefined = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined)
  }

  if (!isPlainRecord(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => [key, stripUndefined(entry)]),
  )
}

export const checklistCanvasNodeRef = (db: Firestore, nodeId: string) =>
  doc(db, CHECKLIST_CANVAS_NODES_COLLECTION, nodeId)

export const checklistCanvasProjectionWriteData = (
  item: ChecklistCanvasProjectionInput,
  options: ChecklistCanvasProjectionOptions = {},
): FirestoreWriteData => stripUndefined(checklistItemToCanvasNode(item, options)) as FirestoreWriteData

export const checklistCanvasNodeWriteData = (node: ChecklistCanvasNode): FirestoreWriteData =>
  stripUndefined(node) as FirestoreWriteData

export const upsertChecklistCanvasProjection = async (
  db: Firestore,
  item: ChecklistCanvasProjectionInput,
  options: ChecklistCanvasProjectionOptions = {},
): Promise<void> => {
  await setDoc(
    checklistCanvasNodeRef(db, item.id),
    checklistCanvasProjectionWriteData(item, options),
    { merge: true },
  )
}

export const queueChecklistCanvasProjectionUpsert = (
  batch: WriteBatch,
  db: Firestore,
  item: ChecklistCanvasProjectionInput,
  options: ChecklistCanvasProjectionOptions = {},
): void => {
  batch.set(
    checklistCanvasNodeRef(db, item.id),
    checklistCanvasProjectionWriteData(item, options),
    { merge: true },
  )
}

export const upsertChecklistCanvasNode = async (
  db: Firestore,
  node: ChecklistCanvasNode,
): Promise<void> => {
  await setDoc(
    checklistCanvasNodeRef(db, node.id),
    checklistCanvasNodeWriteData(node),
    { merge: true },
  )
}

export const queueChecklistCanvasNodeUpsert = (
  batch: WriteBatch,
  db: Firestore,
  node: ChecklistCanvasNode,
): void => {
  batch.set(
    checklistCanvasNodeRef(db, node.id),
    checklistCanvasNodeWriteData(node),
    { merge: true },
  )
}

export const deleteChecklistCanvasProjection = async (
  db: Firestore,
  nodeId: string,
): Promise<void> => {
  await deleteDoc(checklistCanvasNodeRef(db, nodeId))
}

export const queueChecklistCanvasProjectionDelete = (
  batch: WriteBatch,
  db: Firestore,
  nodeId: string,
): void => {
  batch.delete(checklistCanvasNodeRef(db, nodeId))
}

export const checklistCanvasProjectionDisplayForItem = (
  item: Pick<ChecklistItem, 'title' | 'isCompleted' | 'weightage'>
    & Partial<Pick<
      ChecklistItem,
      | 'workflowStatus'
      | 'boardStatus'
      | 'assigneeId'
      | 'assigneeName'
      | 'isHelperVisible'
      | 'complexityStars'
      | 'isUrgentCrisisMode'
      | 'countdownHours'
      | 'timerStartedAt'
      | 'timerExpiredAt'
      | 'isReady'
      | 'resourceReadySku'
    >>,
) => stripUndefined({
  title: item.title,
  isCompleted: item.isCompleted === true,
  workflowStatus: item.workflowStatus ?? null,
  boardStatus: item.boardStatus ?? null,
  weightage: typeof item.weightage === 'number' ? item.weightage : 0,
  assigneeId: item.assigneeId ?? null,
  assigneeName: item.assigneeName ?? null,
  isHelperVisible: item.isHelperVisible,
  complexityStars: item.complexityStars,
  isUrgentCrisisMode: item.isUrgentCrisisMode,
  countdownHours: item.countdownHours ?? null,
  timerStartedAt: item.timerStartedAt ?? null,
  timerExpiredAt: item.timerExpiredAt ?? null,
  isReady: item.isReady,
  resourceReadySku: item.resourceReadySku ?? null,
}) as FirestoreWriteData

export const refreshChecklistCanvasProjectionDisplay = async (
  db: Firestore,
  item: Parameters<typeof checklistCanvasProjectionDisplayForItem>[0] & { id: string },
  audit?: {
    updatedAt?: unknown
    lastUpdatedBy?: string | null
    lastUpdatedByName?: string | null
  },
): Promise<void> => {
  await updateDoc(checklistCanvasNodeRef(db, item.id), stripUndefined({
    display: checklistCanvasProjectionDisplayForItem(item),
    ...audit,
  }) as FirestoreWriteData)
}

export const queueChecklistCanvasProjectionDisplayRefresh = (
  batch: WriteBatch,
  db: Firestore,
  item: Parameters<typeof checklistCanvasProjectionDisplayForItem>[0] & { id: string },
  audit?: {
    updatedAt?: unknown
    lastUpdatedBy?: string | null
    lastUpdatedByName?: string | null
  },
): void => {
  batch.update(checklistCanvasNodeRef(db, item.id), stripUndefined({
    display: checklistCanvasProjectionDisplayForItem(item),
    ...audit,
  }) as FirestoreWriteData)
}

export const buildChecklistDrawingBoardQuery = (db: Firestore, workspaceId: string) =>
  query(
    collection(db, 'checklists'),
    where('workspaceId', '==', workspaceId),
    ...scopedReadConstraints(workspaceId),
  )

export const loadChecklistCanvasProjectionDocs = async (
  db: Firestore,
  workspaceId: string,
  readableChecklistIds: string[],
  projectionQuery: Query,
): Promise<Array<{ id: string; data: () => Record<string, unknown> }>> => {
  if (isRestrictedIn(workspaceId)) {
    const results = await Promise.allSettled(
      readableChecklistIds.map((id) => getDoc(checklistCanvasNodeRef(db, id))),
    )
    const docs: Array<{ id: string; data: () => Record<string, unknown> }> = []
    for (const result of results) {
      if (result.status === 'fulfilled') {
        if (result.value.exists()) {
          docs.push({
            id: result.value.id,
            data: () => result.value.data() as Record<string, unknown>,
          })
        }
      } else {
        const error = result.reason
        const code = error && typeof error === 'object' && 'code' in error ? String((error as { code?: unknown }).code) : ''
        if (code === 'permission-denied' || code === 'not-found') {
          continue
        }
        throw error
      }
    }
    return docs
  }
  const snap = await getDocs(projectionQuery)
  return snap.docs
}

